import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../redux/action';
import ProfileDropdown from "../Profile/ProfileDropdown";
import { hasPermission } from "../../Utills/permission";
import { roleMap, packageMap } from "../../Utills/enums";
import { IconButton } from "../common/Buttons";
import { Menu as MenuIcon, X } from "lucide-react";
import { Moon, Sun } from "lucide-react";
import { useAdminTheme } from "../../styles/ThemeProvider";
import LanguageSelector from "../LanguageSelector/LanguageSelector";

// Import Crisp Vector Icons
import { 
  Home, 
  LayoutDashboard, 
  BarChart3,
  ShoppingCart, 
  UtensilsCrossed, 
  ChevronRight, 
  Pizza, 
  EyeOff, 
  Armchair, 
  Receipt, 
  FileText, 
  History, 
  XCircle, 
  Settings, 
  Users, 
  QrCode, 
  Sliders, 
  Percent, 
  Store, 
  CalendarDays, 
  Bell,
  StickyNotes,
  PlusCircle,
  PackageOpen
} from "lucide-react";

import "./Layout.css";

function Layout({ children }) {
  const { mode, setMode, brand, setBrand } = useAdminTheme();
  const notifications = useSelector(state => state.notifications?.list || []);
  const unreadNotificationCount = notifications.filter(notification => !notification.acknowledged).length;
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeDropdown, setActiveDropdown] = useState(""); 

  // Refs for Sidebar and Toggle Button to handle click-outside
  const sidebarRef = useRef(null);
  const toggleBtnRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("admin_token_info")) || {};
  const navigate = useNavigate();
  const location = useLocation();

  const role = roleMap[user?.roleId];
  const plan = packageMap[user?.packageId];
  const dispatch = useDispatch();
  const canViewSettingMenu = hasPermission(role, plan, "staff") ||
    hasPermission(role, plan, "takeaway-settings");

  useEffect(() => {
    const media = window.matchMedia('(min-width: 769px)');
    const syncSidebar = (event) => {
      setSidebarOpen(event.matches);
      setIsMobile(!event.matches);
    };
    media.addEventListener('change', syncSidebar);
    return () => media.removeEventListener('change', syncSidebar);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen || !isMobile) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const sidebar = sidebarRef.current;
    const toggle = toggleBtnRef.current;
    sidebar?.querySelector('button')?.focus();
    const handleKeys = (event) => {
      if (event.key === 'Escape') setSidebarOpen(false);
      if (event.key !== 'Tab') return;
      const controls = sidebar?.querySelectorAll('a[href], button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled)');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeys);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeys);
      toggle?.focus();
    };
  }, [sidebarOpen, isMobile]);

  // Click & Touch Outside Listener Effect (Desktop + Mobile Support)
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       sidebarOpen &&
  //       sidebarRef.current &&
  //       !sidebarRef.current.contains(event.target) &&
  //       toggleBtnRef.current &&
  //       !toggleBtnRef.current.contains(event.target)
  //     ) {
  //       setSidebarOpen(false);
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   document.addEventListener("touchstart", handleClickOutside);

  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //     document.removeEventListener("touchstart", handleClickOutside);
  //   };
  // }, [sidebarOpen]);

  // Click & Touch Outside Listener Effect (Restricted to Mobile/Tablet ONLY)
useEffect(() => {
  const handleClickOutside = (event) => {
    const isMobileOrTablet = window.innerWidth <= 768; // 👈 Check screen width

    if (
      isMobileOrTablet && // 👈 Only close on mobile/tablet view
      sidebarOpen &&
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target) &&
      toggleBtnRef.current &&
      !toggleBtnRef.current.contains(event.target)
    ) {
      setSidebarOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  document.addEventListener("touchstart", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("touchstart", handleClickOutside);
  };
}, [sidebarOpen]);
  const handleDropdownToggle = (menuName) => {
    if (activeDropdown === menuName) {
      setActiveDropdown(""); 
    } else {
      setActiveDropdown(menuName); 
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.innerWidth <= 768 && setSidebarOpen(false);
  };

  return (
    <div className={`layout-wrapper ${sidebarOpen ? 'sidebar-is-open' : ''}`}>
      <a className="skip-link" href="#admin-main">Skip to content</a>
      {/* ===== HEADER ===== */}
      <header className="app-header">
        <IconButton ref={toggleBtnRef} className="menu-btn" aria-label="Toggle navigation" aria-controls="admin-sidebar" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)} icon={<MenuIcon size={22} />} />
        <div className="header-title">{user.restaurantName}</div>
        <div className="header-right">
          <IconButton aria-label={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')} icon={mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />} />
          <LanguageSelector compact={isMobile} />
          {hasPermission(role, plan, "notification") && (
            <IconButton onClick={() => handleNavigate("/notification")} className="notification-icon" aria-label={`Notifications, ${unreadNotificationCount} unread`}>
              <Bell size={21} />
              <span className="badge">{unreadNotificationCount}</span>
            </IconButton>
          )}
          <div className="profile-icon">
             <ProfileDropdown />
          </div>
        </div>
      </header>

      {/* ===== MOBILE BACKDROP OVERLAY ===== */}
      {sidebarOpen && (
        <div 
          className="sidebar-backdrop" 
          onClick={() => setSidebarOpen(false)} 
        />
      )}

      {/* ===== SIDEBAR ===== */}
      <aside id="admin-sidebar" aria-label="Main navigation" ref={sidebarRef} className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-heading">
          <span>Workspace</span>
          <IconButton onClick={() => setSidebarOpen(false)} aria-label="Close navigation" icon={<X size={18} />} />
        </div>
        
        <ul className="sidebar-menu-list">
          {/* ===== HOME ===== */}
          {hasPermission(role, plan, "home") && (
            <li>
              <Link to="/" className={location.pathname === "/" ? "sidebar-link active" : "sidebar-link"}>
                <Home size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Home
              </Link>
            </li>
          )}

          {/* ===== DASHBOARD ===== */}
          {hasPermission(role, plan, "dashboard") && (
            <li>
              <Link to="/dashboard" className={location.pathname === "/dashboard" ? "sidebar-link active" : "sidebar-link"}>
                <LayoutDashboard size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Sales Summary
              </Link>
            </li>
          )}

          {/* ===== PLACE ORDER (MENU) ===== */}
          {hasPermission(role, plan, "placeOrder") && (
            <li>
              <Link to="/menu" className={location.pathname === "/menu" ? "sidebar-link active" : "sidebar-link"}>
                <ShoppingCart size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Place Order
              </Link>
            </li>
          )}
                  
          {/* ===== MENU MANAGEMENT DROPDOWN ===== */}
          {hasPermission(role, plan, "menu") && (
            <>
              <li 
                className={`sidebar-parent-li ${activeDropdown === "menu" ? "expanded" : ""}`} 
                onClick={() => handleDropdownToggle("menu")}
              >
                <button type="button" className="parent-trigger" aria-expanded={activeDropdown === "menu"}>
                  <UtensilsCrossed size={18} color="var(--ui-warning-text)" className="sidebar-icon" />
                  <span className="parent-text">Menu</span>
                  <ChevronRight size={16} className={`arrow-indicator ${activeDropdown === "menu" ? "rotate" : ""}`} />
                </button>
              </li>
              
              {activeDropdown === "menu" && (
                <ul className="submenu">
                  <li>
                    <Link to="/menu-categories" className={location.pathname === "/menu-categories" ? "sidebar-link active" : "sidebar-link"}>
                      <Pizza size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Categories & Food
                    </Link>
                  </li>
                  <li>
                    <Link to="/menu-status" className={location.pathname === "/menu-status" ? "sidebar-link active" : "sidebar-link"}>
                      <EyeOff size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Item Availability
                    </Link>
                  </li>
                   <li>
                    <Link to="/manage-banner" className={location.pathname === "/manage-banner" ? "sidebar-link active" : "sidebar-link"}>
                      <StickyNotes size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Manage Banner
                    </Link>
                    <Link to="/manage-addons"  className={location.pathname === "/manage-addons" ? "sidebar-link active" : "sidebar-link"}>
                      <PlusCircle size={16} color="var(--ui-warning-text)" className="sidebar-icon" />
                       Add on
                    </Link>
                  </li>
                </ul>
              )}
            </>
          )}

          {/* ===== TABLE MANAGEMENT DROPDOWN ===== */}
          {hasPermission(role, plan, "table") && (
            <>
              <li 
                className={`sidebar-parent-li ${activeDropdown === "table" ? "expanded" : ""}`} 
                onClick={() => handleDropdownToggle("table")}
              >
                <button type="button" className="parent-trigger" aria-expanded={activeDropdown === "table"}>
                  <Armchair size={18} color="var(--ui-warning-text)" className="sidebar-icon" />
                  <span className="parent-text">Table</span>
                  <ChevronRight size={16} className={`arrow-indicator ${activeDropdown === "table" ? "rotate" : ""}`} />
                </button>
              </li>

              {activeDropdown === "table" && (
                <ul className="submenu">
                  {hasPermission(role, plan, "tables") && (
                    <li>
                      <Link to="/tables" className={location.pathname === "/tables" ? "sidebar-link active" : "sidebar-link"}>
                        <Sliders size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Table Order
                      </Link>
                    </li>
                  )}
                  {hasPermission(role, plan, "bills") && (
                    <li>
                      <Link to="/bills" className={location.pathname === "/bills" ? "sidebar-link active" : "sidebar-link"}>
                        <Receipt size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Bill
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
            {/* ===== Bill ===== */}
                    {hasPermission(role, plan, "bills")  && !hasPermission(role, plan, "table") &&(
                    <li>
                      <Link to="/bills" className={location.pathname === "/bills" ? "sidebar-link active" : "sidebar-link"}>
                        <Receipt size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Bill
                      </Link>
                    </li>
                  )}

          {/* ===== KOT DROPDOWN ===== */}
          {hasPermission(role, plan, "KOT") && (
            <>
              <li 
                className={`sidebar-parent-li ${activeDropdown === "kot" ? "expanded" : ""}`} 
                onClick={() => handleDropdownToggle("kot")}
              >
                <button type="button" className="parent-trigger" aria-expanded={activeDropdown === "kot"}>
                  <FileText size={18} color="var(--ui-warning-text)" className="sidebar-icon" />
                  <span className="parent-text">KOT</span>
                  <ChevronRight size={16} className={`arrow-indicator ${activeDropdown === "kot" ? "rotate" : ""}`} />
                </button>
              </li>

              {activeDropdown === "kot" && (
                <ul className="submenu">
                  <li>
                    <Link to="/live-order" className={location.pathname === "/live-order" ? "sidebar-link active" : "sidebar-link"}>
                      <FileText size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Live KOT
                    </Link>
                  </li>
                  <li>
                    <Link to="/order-history" className={location.pathname === "/order-history" ? "sidebar-link active" : "sidebar-link"}>
                      <History size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> KOT History
                    </Link>
                  </li>
                  <li>
                    <Link to="/order-cancelled" className={location.pathname === "/order-cancelled" ? "sidebar-link active" : "sidebar-link"}>
                      <XCircle size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Cancelled KOT
                    </Link>
                  </li>
                </ul>
              )}
            </>
          )}

          {hasPermission(role, plan, "reports") && (
            <li>
              <Link to="/reports" className={location.pathname === "/reports" ? "sidebar-link active" : "sidebar-link"}>
                <BarChart3 size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Reports
              </Link>
            </li>
          )}

          {hasPermission(role, plan, "shift-closing") && (
            <li>
              <Link to="/shift-closing" className={location.pathname === "/shift-closing" ? "sidebar-link active" : "sidebar-link"}>
                <Receipt size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Shift Closing
              </Link>
            </li>
          )}

          {/* ===== TAKEAWAY DROPDOWN ===== */}
          {hasPermission(role, plan, "takeaway-orders") && (
            <>
              <li className={`sidebar-parent-li ${activeDropdown === "takeaway" ? "expanded" : ""}`} onClick={() => handleDropdownToggle("takeaway")}>
                <button type="button" className="parent-trigger" aria-expanded={activeDropdown === "takeaway"}>
                  <PackageOpen size={18} color="var(--ui-warning-text)" className="sidebar-icon" />
                  <span className="parent-text">Takeaway</span>
                  <ChevronRight size={16} className={`arrow-indicator ${activeDropdown === "takeaway" ? "rotate" : ""}`} />
                </button>
              </li>
              {activeDropdown === "takeaway" && <ul className="submenu">
                {hasPermission(role, plan, "takeaway-create") && <li><Link to="/takeaway/new" className={location.pathname === "/takeaway/new" ? "sidebar-link active" : "sidebar-link"}><PlusCircle size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> New Order</Link></li>}
                <li><Link to="/takeaway/orders" className={location.pathname === "/takeaway/orders" ? "sidebar-link active" : "sidebar-link"}><FileText size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Live Orders</Link></li>
                {hasPermission(role, plan, "takeaway-history") && <li><Link to="/takeaway/history" className={location.pathname === "/takeaway/history" ? "sidebar-link active" : "sidebar-link"}><History size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> History</Link></li>}
                {hasPermission(role, plan, "takeaway-cancelled") && <li><Link to="/takeaway/cancelled" className={location.pathname === "/takeaway/cancelled" ? "sidebar-link active" : "sidebar-link"}><XCircle size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Cancelled</Link></li>}
              </ul>}
            </>
          )}

          {/* ===== SETTINGS DROPDOWN ===== */}
          {canViewSettingMenu && (
            <>
              <li 
                className={`sidebar-parent-li ${activeDropdown === "setting" ? "expanded" : ""}`} 
                onClick={() => handleDropdownToggle("setting")}
              >
                <button type="button" className="parent-trigger" aria-expanded={activeDropdown === "setting"}>
                  <Settings size={18} color="var(--ui-warning-text)" className="sidebar-icon" />
                  <span className="parent-text">Setting</span>
                  <ChevronRight size={16} className={`arrow-indicator ${activeDropdown === "setting" ? "rotate" : ""}`} />
                </button>
              </li>

              {activeDropdown === "setting" && (
                <ul className="submenu">
                    {hasPermission(role, plan, "user-alter") && (
                  <li>
                    <Link to="/user-alter" className={location.pathname === "/user-alter" ? "sidebar-link active" : "sidebar-link"}>
                      <Users size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Staff Role
                    </Link>
                  </li>
                   )}
                   
                   {hasPermission(role, plan, "qr-code") && (
                  <li>
                    <Link to="/qr-code" className={location.pathname === "/qr-code" ? "sidebar-link active" : "sidebar-link"}>
                      <QrCode size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> QR Code
                    </Link>
                  </li>
                   )}

                  {hasPermission(role, plan, "table-setting") && (
                  <li>
                    <Link to="/table-setting" className={location.pathname === "/table-setting" ? "sidebar-link active" : "sidebar-link"}>
                      <Armchair size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Table
                    </Link>
                  </li>
                   )}

                  {hasPermission(role, plan, "tax-setting") && <li>
                    <Link to="/tax-setting" className={location.pathname === "/tax-setting" ? "sidebar-link active" : "sidebar-link"}>
                      <Percent size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Tax
                    </Link>
                  </li>}
                  {hasPermission(role, plan, "restaurant-setting") && <li>
                    <Link to="/restaurant-setting" className={location.pathname === "/restaurant-setting" ? "sidebar-link active" : "sidebar-link"}>
                      <Store size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Restaurant
                    </Link>
                  </li>}
                  {hasPermission(role, plan, "takeaway-settings") && (
                  <li>
                    <Link to="/takeaway-setting" className={location.pathname === "/takeaway-setting" ? "sidebar-link active" : "sidebar-link"}>
                      <PackageOpen size={16} color="var(--ui-warning-text)" className="sidebar-icon" /> Takeaway
                    </Link>
                  </li>
                  )}
                </ul>
              )}
            </>
          )}

          {/* ===== STANDALONE LINKS ===== */}
          {hasPermission(role, plan, "reservation") && (
            <li>
              <Link to="/reservation" className={location.pathname === "/reservation" ? "sidebar-link active" : "sidebar-link"}>
                <CalendarDays size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Reservation
              </Link>
            </li>
          )}

          {hasPermission(role, plan, "waiterRequest") && (
            <li>
              <Link to="/waiterRequest" className={location.pathname === "/waiterRequest" ? "sidebar-link active" : "sidebar-link"}>
                <Bell size={18} color="var(--ui-warning-text)" className="sidebar-icon" /> Waiter Request
              </Link>
            </li>
          )}
        </ul>

        <div className="sidebar-footer">
          <label htmlFor="admin-accent">Accent color</label>
          <select id="admin-accent" value={brand} onChange={event => setBrand(event.target.value)}>
            <option value="teal">Teal</option>
            <option value="indigo">Indigo</option>
          </select>
          <p>Powered by ARK tech</p>
        </div>
      </aside>

      {/* ===== MAIN CONTENT ===== */}
      <main id="admin-main" tabIndex={-1} className="main-content">
        {children}
      </main>
    </div>
  );
}

export default Layout;
